/**
 * Card Product Hover Sizes
 * Handles adding products to cart by clicking size buttons
 */

class CardHoverSizes {
  constructor() {
    this.init();
    this.initializeCartCount();
  }

  init() {
    // Listen for ALL clicks on card wrappers with hover sizes
    document.addEventListener('click', (e) => {
      const cardWrapper = e.target.closest('.card-wrapper');

      // Only handle cards that have hover sizes
      if (!cardWrapper || !cardWrapper.querySelector('.card-product-hover-sizes')) return;

      // Check if click is on quick-add button or modal-opener FIRST
      const quickAddButton = e.target.closest('.quick-add__submit, modal-opener, .quick-add');
      if (quickAddButton) {
        // Don't interfere with quick-add functionality - let it open the modal
        return;
      }

      // Check if click is on a size button
      const button = e.target.closest('.card-product-hover-sizes__button');
      if (button && !button.disabled && !button.classList.contains('loading')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.handleSizeClick(button);
        return false;
      }

      // If click is NOT on a button, manually navigate to product page
      // Try to find a visible product link - there may be multiple .card__heading elements
      const productLinks = cardWrapper.querySelectorAll('.card__heading a');
      let productLink = null;

      // Find the first link with a valid href
      for (const link of productLinks) {
        if (link.href && link.href !== '' && link.href !== window.location.href) {
          productLink = link;
          break;
        }
      }

      if (productLink && productLink.href) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = productLink.href;
      }
    }, true); // Use capture phase to intercept BEFORE the link's ::after overlay
  }

  initializeCartCount() {
    // Fetch initial cart count on page load
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.updateCartCount(cart.item_count);
      })
      .catch(error => {
        console.error('Error fetching initial cart:', error);
      });
  }

  async handleSizeClick(button) {
    const variantId = parseInt(button.dataset.variantId, 10);

    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    // Prevent multiple clicks
    button.disabled = true;

    // Find the card wrapper to append the message
    const cardWrapper = button.closest('.card-wrapper');
    if (!cardWrapper) {
      button.disabled = false;
      return;
    }

    // Create and show loading message
    const loadingMessage = this.createMessage('Agregando al carrito...', cardWrapper);

    try {
      // Add to cart using Shopify Ajax API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      await response.json();

      // Remove loading message and show success
      this.removeMessage(loadingMessage);
      const successMessage = this.createMessage('¡Agregado al carrito!', cardWrapper, 'success');

      // Trigger cart update events
      this.triggerCartUpdate();

      // Re-enable button
      button.disabled = false;

      // Remove success message after delay
      setTimeout(() => {
        this.removeMessage(successMessage);
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);

      // Remove loading message and show error
      this.removeMessage(loadingMessage);
      const errorMessage = this.createMessage('Error al agregar', cardWrapper, 'error');

      // Re-enable button
      button.disabled = false;

      // Remove error message after delay
      setTimeout(() => {
        this.removeMessage(errorMessage);
      }, 2000);
    }
  }

  createMessage(text, container, type = 'loading') {
    const message = document.createElement('div');
    message.className = `card-hover-sizes-message card-hover-sizes-message--${type}`;
    message.textContent = text;
    container.appendChild(message);

    // Trigger animation
    setTimeout(() => {
      message.classList.add('card-hover-sizes-message--visible');
    }, 10);

    return message;
  }

  removeMessage(message) {
    if (message && message.parentNode) {
      message.classList.remove('card-hover-sizes-message--visible');
      setTimeout(() => {
        message.remove();
      }, 300);
    }
  }

  triggerCartUpdate() {
    // Fetch cart data
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update cart count bubble
        this.updateCartCount(cart.item_count);

        // Trigger cart drawer update by publishing event
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'card-hover-sizes',
            cartData: cart
          });
        }

        // Dispatch custom event for other listeners
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart }
          })
        );

        // Update cart drawer using Section Rendering API
        const sectionsToRender = [
          {
            section: 'cart-drawer',
            selector: '.drawer__inner'
          }
        ];

        const sectionsUrl = sectionsToRender
          .map((section) => `sections[]=${section.section}`)
          .join('&');

        fetch(`${window.location.pathname}?${sectionsUrl}`)
          .then(response => response.json())
          .then(response => {
            sectionsToRender.forEach(section => {
              const drawerInner = document.querySelector('#CartDrawer ' + section.selector);
              if (drawerInner && response[section.section]) {
                const html = new DOMParser()
                  .parseFromString(response[section.section], 'text/html')
                  .querySelector(section.selector);

                if (html) {
                  drawerInner.replaceWith(html);
                }
              }
            });

            // Remove is-empty class from cart-drawer element
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cart.item_count > 0) {
              cartDrawer.classList.remove('is-empty');
            }
          })
          .catch(error => {
            console.error('Error updating cart drawer:', error);
          });
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
      });
  }

  updateCartCount(count) {
    // Update the cart count bubble
    const cartBubble = document.querySelector('.cart-count-bubble');
    if (cartBubble) {
      const countSpans = cartBubble.querySelectorAll('span');
      countSpans.forEach(span => {
        if (span.getAttribute('aria-hidden') === 'true') {
          span.textContent = count;
        } else if (span.classList.contains('visually-hidden')) {
          span.textContent = `${count} artículo${count !== 1 ? 's' : ''}`;
        }
      });

      // Show or hide the bubble based on count
      if (count > 0) {
        cartBubble.style.display = '';
      } else {
        cartBubble.style.display = 'none';
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CardHoverSizes();
  });
} else {
  new CardHoverSizes();
}
